import * as React from 'react';

import { Form } from '@independent-software/typeui/controls/Form';
import { Dialog } from '@independent-software/typeui/controls/Dialog';
import { Panel } from '@independent-software/typeui/controls/Panel';
import { DataTable } from '@independent-software/typeui/controls/DataTable';
import { Checkbox } from '@independent-software/typeui/controls/Checkbox';
import { Datum } from '@independent-software/typeui/formatters/Datum';
import { Dropdown } from '@independent-software/typeui/controls/Dropdown';
import { Input } from '@independent-software/typeui/controls/Input';

import { Sample, SampleFactory, Campaign, CampaignFactory, Station, StationFactory } from '../../resource/'; 
import { List, IListState, IListProps, Query, SearchFilter } from '../../services/';

import { IAuth } from '../../AuthContext';
import { IFilter } from '../../FilterContext';
import { IconBar, Export } from '../../modules';
import { Gate } from '../../controls/Gate';

interface IListSamplesProps extends IListProps {
  filter: IFilter;
  campaignId?: number;
  stationId?: number;
  /**
   * Event is fired when a sample is clicked.
   */
  onClick?: (sample:Sample) => void;
}

interface IListSamplesState extends IListState<Sample> {
  /**
   * Data for campaigns dropdown
   */
   campaigns: Campaign[];    
  /**
   * Data for stations dropdown
   */
   stations: Station[];     
}

class ListSamples extends List<Sample, IListSamplesProps, IListSamplesState> {
  constructor(props: IAuth & IListSamplesProps) {
    super(props, SampleFactory, 'date', 'desc');

    // Initialize state (list initializes its own part of the state):
    this.state = {
      ...this.state
    };

    if(this.props.campaignId) {
      this.setFilter('campaign', 'eq', { id: this.props.campaignId }, true);
    }       
    if(this.props.stationId) {
      this.setFilter('station', 'eq', { id: this.props.stationId }, true);
    }           

    this.setFilter('system', 'in', this.props.filter.activeSystems.join(','));
  }

  componentDidMount() {
    this.handleCampaignSearch();    
    this.handleStationSearch();    
  }    

  handleSearch = (value:string) => {
    this.setFilter('q', 'like', value);
  }  

  handleChangeCampaign = (value: Campaign) => {
    this.setFilter('campaign', 'eq', value);
  }

  handleCampaignSearch = (q?:string) => {
    // Retrieve a list of campaigns:
    let query = new Query('name', 'asc');
    query.setFilter('system', 'in', this.props.filter.activeSystems.join(','));
    if (q) query.setFilter('q', 'like', q);
    CampaignFactory.getSome(this.props.auth, 0, 999, query)
      .then((res) => this.setState({ campaigns: res.items }));
  }    

  handleChangeStation = (value: Station) => {
    this.setFilter('station', 'eq', value);
  }

  handleStationSearch = (q?:string) => {
    // Retrieve a list of stations:
    let query = new Query('name', 'asc');
    query.setFilter('system', 'in', this.props.filter.activeSystems.join(','));
    if (q) query.setFilter('q', 'like', q);
    StationFactory.getSome(this.props.auth, 0, 999, query)
      .then((res) => this.setState({ stations: res.items }));
  }     

  handleChangeFromDate = (value:string) => {
    this.setFilter('date', 'gte', value);
  }  

  handleChangeToDate = (value:string) => {
    this.setFilter('date', 'lte', value);
  }      

  render() {
    let p = this.props;

    let filter = 
    <React.Fragment>
      <Panel.Content>
        <Form.Uncontrolled hint="Type to search records">
          <SearchFilter value={this.getFilter('q', 'like')} onSearch={this.handleSearch}/>
        </Form.Uncontrolled>
        {!p.campaignId && 
          <Form.Uncontrolled hint="Filter by campaign">
            <Dropdown onSearch={this.handleCampaignSearch} name="campaign" fluid clearable value={this.getFilter('campaign', 'eq')} data={this.state.campaigns} placeholder="Campaign" label={(item:Campaign) => item.name} onChange={this.handleChangeCampaign}>
              <Dropdown.Column weight={1}>{(item:Campaign) => item.name}</Dropdown.Column>
            </Dropdown>
          </Form.Uncontrolled>}     
        {!p.stationId && 
          <Form.Uncontrolled hint="Filter by station">
            <Dropdown onSearch={this.handleStationSearch} name="station" fluid clearable value={this.getFilter('station', 'eq')} data={this.state.stations} placeholder="Station" label={(item:Station) => item.name} onChange={this.handleChangeStation}>
              <Dropdown.Column weight={1}>{(item:Station) => item.name}</Dropdown.Column>
            </Dropdown>
          </Form.Uncontrolled>}                     
        <Form.Uncontrolled hint="Filter by start date">
          <Input name="from" type="date" fluid clearable value={this.getFilter('date', 'gte')} placeholder="From date" onChange={this.handleChangeFromDate}/>
        </Form.Uncontrolled>
        <Form.Uncontrolled hint="Filter by end date">
          <Input name="to" type="date" fluid clearable value={this.getFilter('date', 'lte')} placeholder="To date" onChange={this.handleChangeToDate}/>
        </Form.Uncontrolled>          
      </Panel.Content>
      <Panel.Footer>
        <div style={{display: "flex", justifyContent: 'space-between'}}>
          <Gate right='can_delete_samples'>
            <Form.Uncontrolled>
              <Checkbox label="Include deleted items" name="trash" type="toggle" checked={this.getTrash()} onChange={this.handleTrash}/>
            </Form.Uncontrolled>
          </Gate>
          <Export onExport={this.handleExport}/>
        </div>
      </Panel.Footer>      
    </React.Fragment>

    return (
      <React.Fragment>
        <IconBar>
          <Panel.Icon icon="tools" width={350}>
            {filter}
          </Panel.Icon>
        </IconBar> 
        <DataTable error={this.state.error} loading={this.state.loading} scrollTop={this.state.scrollTop} onScroll={this.handleScroll} data={this.state.items} onFetch={this.handleFetch} onClick={p.onClick} onOrder={this.handleOrder} order={this.getOrder()} dir={this.getDir()}>
          <DataTable.Column weight={1} label="Date" order="date" dir="desc">{(item:Sample) => <Datum.LongDate value={item.date}/>}</DataTable.Column>
          <DataTable.Column weight={1} label="Time" order="time" dir="desc">{(item:Sample) => <Datum.Time value={item.time}/>}</DataTable.Column>
          <DataTable.Column weight={1} label="Campaign" order="campaign" dir="asc">{(item:Sample) => item.campaign.name}</DataTable.Column>
          <DataTable.Column weight={1} label="Station" order="station" dir="asc">{(item:Sample) => item.station.name}</DataTable.Column>
        </DataTable>
        <Dialog.Xhr open={this.state.exportError != null} error={this.state.exportError} onClose={this.handleCloseDialog}/>
      </React.Fragment>
    );
  }
}

export { ListSamples };
